import type { ConditionalFormattingModification } from '../store/schema/ConditionalFormattingRule';
import { ConditionalFormattingModificationType } from '../store/schema/ConditionalFormattingModificationType';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type CategoryColor from 'owa-categories/lib/store/schema/CategoryColor';
import { type ConditionalFormattingColorValue } from './ConditionalFormattingCategoryColor';
import { lightColorCodeValueMap } from './getConditionalFormattingLightThemeColorCodeValueMap';
import { darkColorCodeValueMap } from './getConditionalFormattingDarkThemeColorCodeValueMap';

export enum ConditionalFormattingSurface {
    Text = 'text',
    Icon = 'icon',
    Button = 'button',
}

/**
 * Unified conditional formatting style resolver.
 * Applies formatting modifications based on the target surface.
 */
export function getConditionalFormattingStyles(
    surface: ConditionalFormattingSurface,
    modifications: ConditionalFormattingModification[] | undefined,
    isDarkTheme: boolean,
    isSelected: boolean = false
): React.CSSProperties | undefined {
    if (!modifications || modifications.length === 0) {
        return undefined;
    }

    const colorMap = isDarkTheme ? darkColorCodeValueMap : lightColorCodeValueMap;

    const css: React.CSSProperties = {};

    for (const mod of modifications) {
        const colorEntry = colorMap[mod.value as CategoryColor];

        // Skip if color entry is required but not found
        if (mod.type !== ConditionalFormattingModificationType.FontFamily && !colorEntry) {
            continue; // Changed from break to continue to process remaining modifications
        }

        applyModification(mod, colorEntry, css, surface, isSelected);
    }

    return Object.keys(css).length > 0 ? css : undefined;
}

/**
 * Apply a single modification to the CSS properties
 */
function applyModification(
    mod: ConditionalFormattingModification,
    colorEntry: ConditionalFormattingColorValue,
    css: React.CSSProperties,
    surface: ConditionalFormattingSurface,
    isSelected: boolean
): void {
    const isV2Enabled = isGlobalFeatureEnabled('tri-conditional-formatting-v2');
    switch (mod.type) {
        case ConditionalFormattingModificationType.ColorText:
            applyColorModification(css, colorEntry, surface);
            break;

        case ConditionalFormattingModificationType.ColorTextAndBackground:
            if (isV2Enabled) {
                applyColorTextAndBackgroundModification(css, colorEntry, surface, isSelected);
            }
            break;

        case ConditionalFormattingModificationType.FontFamily:
            if (surface === ConditionalFormattingSurface.Text && isV2Enabled) {
                css.fontFamily = mod.value as string;
            }
            break;
    }
}

/**
 * Apply color modification based on surface.
 * CategoryColor.BlackWhite is a theme-adaptive high-contrast color
 * that renders #242424 in light theme and #FFFFFF in dark theme.
 */
function applyColorModification(
    css: React.CSSProperties,
    colorEntry: ConditionalFormattingColorValue,
    surface: ConditionalFormattingSurface
): void {
    if (surface === ConditionalFormattingSurface.Button) {
        css.backgroundColor = colorEntry.iconColor;
    } else {
        css.color = colorEntry.iconColor;
    }
}

/**
 * Apply color text and background modification based on surface
 */
function applyColorTextAndBackgroundModification(
    css: React.CSSProperties,
    colorEntry: ConditionalFormattingColorValue,
    surface: ConditionalFormattingSurface,
    isSelected: boolean
): void {
    const surfaceStyleMap = {
        [ConditionalFormattingSurface.Text]: {
            color: isSelected ? colorEntry.textSelectedHoverColor : colorEntry.textColor,
        },
        [ConditionalFormattingSurface.Icon]: { color: colorEntry.iconColor },
        [ConditionalFormattingSurface.Button]: { backgroundColor: colorEntry.buttonColor },
    };

    Object.assign(css, surfaceStyleMap[surface]);
}
